<template>
  <div
    ref="contentBox"
    class="flex flex-col lg:block content-box-wrapper relative"
    :class="[
      noPadding ? 'py-0' : largePadding ? 'lg:py-12' : 'lg:py-20',
      { 'py-12 lg:py-0': mobileVerticalPadding },
    ]"
  >
    <div
      class="container"
      :class="[
        { 'order-2': largeImage && visualRight },
        { 'py-medium': largePadding },
        { 'pt-semi lg:pt-0': !largePadding && !mobileVerticalPadding },
      ]"
    >
      <div
        data-test-id="block-box"
        class="grid content-box-grid"
        :class="{
          'visual-first': visualFirstCheck,
          'visual-right': visualRight,
          'single-column': !hasVisual && !largeImage,
          'no-body': !hasBody,
        }"
      >
        <div
          v-if="hasVisual"
          class="content-box-visual"
          :class="{
            'flex flex-wrap justify-center items-center': visualCentralized,
          }"
          data-test-id="block-visual"
          :style="{
            'min-height':
              $device.isDesktop && fullHeightImage ? '450px' : 'auto',
          }"
        >
          <!-- @slot The visual content to be displayed -->
          <slot name="visual" />
        </div>
        <div
          data-test-id="block-header"
          class="content-box-header text-center text-smallest leading-none lg:w-9/12 md:w-6/12 mx-auto lg:mt-10"
          :class="{
            'lg:text-left': hasVisual || largeImage,
            'py-10 px-5 lg:p-0': hasHeaderBg,
            [getHeaderBgClass]: hasHeaderBg,
          }"
        >
          <SharedTextTagLine
            v-if="tagline"
            :tagline="tagline"
            :text-variant="textVariant"
            data-test-id="text-tagline"
          />
          <SharedTextSectionHeader
            :title="title"
            :text-variant="variant"
            :title-color="titleColor ? titleColor : ''"
            data-test-id="text-title"
          />
        </div>
        <img
          v-if="largeImage && mobileImageBelowHeadline"
          :src="largeImage"
          class="lg:hidden"
        >
        <div
          v-if="hasBody"
          class="content-box-content content-layout-content m-0 text-left text-smaller md:text-base font-gt-america leading-normal lg:w-9/12 md:w-6/12 mx-auto"
          :class="[
            textClass,
            { 'lg:mb-10': !largePadding },
            thinFont ? 'font-extralight' : 'font-light',
          ]"
          :style="{ color: textColor ? textColor : '' }"
          data-test-id="block-body"
        >
          <div v-if="showParsed">
            <component
              :is="el.type"
              v-for="(el, index) in bodyText"
              :key="index"
              class="mb-6"
            >
              <template v-if="el.text">
                {{ el.text }}
              </template>
              <template v-else>
                <li
                  v-for="(text, i) in el.items"
                  :key="i"
                  class="pl-6 relative mb-2"
                >
                  {{ text }}
                </li>
              </template>
            </component>
          </div>

          <div v-if="!showParsed" v-html="bodyText" />

          <div
            v-if="showCtaButton"
            class="text-center font-normal"
            :class="{
              'lg:text-left': hasVisual || largeImage,
            }"
            data-test-id="block-action"
          >
            <MajorityUiMajorityLink
              v-if="to"
              :to="to"
              class="btn mt-8 mx-auto lg:mx-0 lg:w-auto"
              :class="[btnVariantClass, mobileLargeCta ? ['w-full'] : '']"
              tracking-name="contentBoxButton"
              tracking-type="cta"
              :text="getCtaText"
              :aria-label="getBtnDesc"
              data-test-id="button-action"
            />
            <button
              v-else
              class="btn mt-8 mx-auto lg:mx-0 lg:w-auto"
              :class="[btnVariantClass, mobileLargeCta ? ['w-full'] : '']"
              data-test-id="button-action"
              :aria-label="getBtnDesc"
              @click.prevent="onActionTrigger"
            >
              {{ getCtaText }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="largeImage"
      class="order-first lg:order-none lg:absolute bg-no-repeat bg-cover bg-center full-screen w-full lg:w-1/2 h-full top-0 full-image"
      :class="[
        { 'right-0': largeImage && visualRight },
        mobileImageBelowHeadline ? ['hidden lg:block'] : '',
      ]"
      :style="`background-image: url(${largeImage})`"
    />
    <div
      v-if="bgAnimation === 'none'"
      class="content-box-background absolute -z-10 inset-0 flex justify-center items-center overflow-hidden"
      :class="[getBgClass]"
      data-test-id="block-animation"
    />
    <div
      v-else-if="bgAnimation === 'circle'"
      class="absolute -z-10 inset-0 flex justify-center items-center overflow-hidden"
      data-test-id="block-animation"
    >
      <div class="ar-square w-screen h-full relative">
        <div
          class="content-box-background circle absolute inset-0 block rounded-full"
          :class="[getBgClass, { ready: isReady }]"
        />
      </div>
    </div>
    <div
      v-else-if="bgAnimation === 'rectangle'"
      class="absolute -z-10 inset-0 flex justify-center items-end overflow-hidden"
      data-test-id="block-animation"
    >
      <div
        class="content-box-background rect absolute inset-x-0 bottom-0 h-full block"
        :class="[getBgClass, { ready: isReady }]"
      />
    </div>
  </div>
</template>

<script>
import { isNull, isNullOrUndefined } from '@/utils/comparators'
import { isDarkOrLightByElement } from '@/utils/contrast-calculator'

export default {
  props: {
    /**
     * The text on top of title
     */
    tagline: {
      type: String,
      default: null
    },
    /**
     * Title
     */
    title: {
      type: String,
      required: true
    },
    /**
     * Content
     */
    body: {
      type: [String, Array],
      required: false,
      default: ''
    },
    /**
     * Text of CTA
     */
    cta: {
      type: [String, Boolean],
      default: false
    },
    /**
     * Value for a route to be triggered in the button click
     */
    to: {
      type: [String, Object],
      default: ''
    },
    /**
     * Background class
     */
    bgClass: {
      type: String,
      default: 'bg-white'
    },
    btnDesc: {
      type: String,
      default: 'Majority'
    },
    /**
     * Background animation
     * Only works in mobile / tablet
     */
    bgAnimation: {
      type: String,
      default: 'none',
      validator: val => ['none', 'circle', 'rectangle'].includes(val)
    },
    /**
     * Title background class
     */
    headerBgClass: {
      type: String,
      default: () => null
    },
    /**
     * Button variant class
     */
    btnVariantClass: {
      type: String,
      default: 'btn-infra-violette'
    },
    /**
     * Text variant class
     */
    textVariant: {
      type: String,
      default: null,
      validator: val => ['light', 'dark', 'gray', 'null'].includes(val)
    },
    /**
     * If should show visual first than the content
     */
    visualFirst: {
      type: Boolean,
      default: false
    },
    /**
     * If should show the visual on the right
     * Only works in desktop
     */
    visualRight: {
      type: Boolean,
      default: false
    },
    /**
     * If should center the visual in middle of div
     */
    visualCentralized: {
      type: Boolean,
      default: false
    },
    /* Image appears at 50% screen width on desktop if below exists */
    largeImage: {
      type: String,
      default: ''
    },
    mobileImageBelowHeadline: {
      type: Boolean,
      default: false
    },
    mobileLargeCta: {
      type: Boolean,
      default: false
    },
    mobileVerticalPadding: {
      type: Boolean,
      default: true
    },
    largePadding: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    thinFont: {
      type: Boolean,
      default: false
    },
    fullHeightImage: {
      type: Boolean,
      default: false
    },
    titleColor: {
      type: String,
      default: ''
    },
    textColor: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isReady: false,
      calculatedTextVariant: 'dark',
      visualFirstCheck: false,
      showParsed: false
    }
  },
  computed: {
    hasVisual () {
      return !!this.$slots.visual
    },
    textClass () {
      if (this.calculatedTextVariant === 'null') {
        return ''
      }

      if (this.textVariant === 'gray') {
        return 'text-light-gray'
      }

      return (this.textVariant || this.calculatedTextVariant) === 'light'
        ? 'text-white'
        : 'text-dark'
    },
    variant () {
      return this.textVariant || this.calculatedTextVariant
    },
    bodyText () {
      //  check if body has the tags required to parse
      if (this.body.includes('\n+') || this.body.includes('\n*')) {
        const result = []
        const chunks = Array.isArray(this.body)
          ? this.body
          : this.body.split('\n')
        chunks.forEach((e) => {
          if (e.startsWith('+') || e.startsWith('*')) {
            const type = e.startsWith('+') ? 'ol' : 'ul'
            const text = e.substring(1).trim()

            if (
              result.length === 0 ||
              result[result.length - 1].type !== type
            ) {
              result.push({ type, items: [text] })
            } else if (result[result.length - 1].type === type) {
              result[result.length - 1].items.push(text)
            }
          } else {
            result.push({ type: 'p', text: e })
          }
        })
        this.setParsed(true)
        return result
      } else {
        this.setParsed(false)
        return this.body
      }
    },
    hasBody () {
      return (this.bodyText.length > 0)
    },
    showCtaButton () {
      if (this.hasLinkOrAction) {
        return true
      }

      if (typeof this.cta === 'boolean') {
        return this.cta
      }
      return false
    },
    hasLinkOrAction () {
      return (
        this.to || (this.$attrs && this.$attrs.onAction) !== undefined
      )
    },
    hasHeaderBg () {
      return (
        !isNullOrUndefined(this.headerBgClass) &&
        this.headerBgClass !== 'bg-white' &&
        !this.hasVisual
      )
    },
    getHeaderBgClass () {
      return `${this.headerBgClass} lg:bg-white`
    },
    getBgClass () {
      const temporaryBgClass = this.bgClass === 'bg-transparent' ? 'bg-white' : this.bgClass
      if (this.hasHeaderBg) {
        return `lg:${temporaryBgClass}`
      }

      return temporaryBgClass
    },
    getBtnDesc () {
      return this.getCtaText + ' - ' + this.btnDesc
    },
    getCtaText () {
      return this.cta || this.$messages('action.learn_more')
    }
  },
  created () {
    this.visualFirstCheck = this.visualFirst
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
    this.calculateTextVariant()
    this.handleVisualFirst()

    const observer = this.initializeIntersectionObserver()
    if (!isNull(observer)) {
      observer.observe(this.$el)
    } else {
      this.isReady = true
    }
  },
  unmounted () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    setParsed (value) {
      this.showParsed = value
    },
    onActionTrigger () {
      /**
       * Action event is triggered when the CTA button is clicked.
       *
       * @event actione
       */
      this.$emit('action')
      this.$track.linkClicked('contentBoxButton', 'cta', {
        linkText: this.getCtaText
      })
    },
    handleResize () {
      this.calculateTextVariant()
      this.handleVisualFirst()
    },
    handleVisualFirst () {
      const windowWidth = window.innerWidth
      if (windowWidth >= 1024) {
        this.visualFirstCheck = false
      }
    },
    calculateTextVariant () {
      try {
        if (!this.titleColor || !this.textColor) {
          this.calculatedTextVariant = isDarkOrLightByElement(
            this.$refs.contentBox.querySelector('.content-box-background')
          )
        } else {
          this.calculatedTextVariant = 'null'
        }
      } catch (error) {
        this.calculatedTextVariant = 'dark'
      }
    },
    initializeIntersectionObserver () {
      try {
        return new IntersectionObserver(this.intersectionObserverCallback, {
          threshold: 0.4
        })
      } catch (error) {
        return null
      }
    },
    intersectionObserverCallback (entries, observer) {
      const [entry] = entries
      if (entry && entry.isIntersecting) {
        this.isReady = true
      }
    }
  }
}
</script>
<style>
.content-layout-content p {
  margin-bottom: 1.5rem !important;
}
.content-layout-content li {
  padding-left: 1.5rem !important;
  margin-bottom: 0.5rem!important;
  counter-increment: content-box-list-counter;
}
.content-layout-content ol {
  position: relative;
  counter-reset: content-box-list-counter;
}
.content-layout-content ul {
  position: relative;
  counter-reset: content-box-list-counter;
}

.content-layout-content li::before {
  width: 1em !important;
  margin-right: 0 !important;
  position: absolute !important;
  left: 0 !important;
}
.content-layout-content ol li::before {
  content: counter(content-box-list-counter) ". ";
  font-family: Druk, Impact, sans-serif !important;
}
.content-layout-content ul li::before {
   content: "\e00c";
   font-family: "majority-dsm";
   margin-top: 2px;
   font-size: .75rem;
}
</style>

<style lang="postcss" scoped>

.circle {
  transition: transform 2s, opacity 1s;
  @apply: ease-in-out-quart;
  transform: scale(0);
  opacity: 0;

  &.ready {
    opacity: 1;
    transform: scale(3);
  }

  /* Disable background animation on dektop */
  @screen lg {
    opacity: 1;
    transform: scale(3);
  }
}

.rect {
  transition: max-height 1.5s;
  @apply: ease-in-out-quart;
  max-height: 50%;

  &.ready {
    max-height: 100%;
  }

  /* Disable background animation on dektop */
  @screen lg {
    max-height: 100%;
  }
}

.content-box-header {
  grid-area: header;
  align-self: end;
}

.content-box-visual {
  grid-area: visual;
}

.content-box-content {
  grid-area: content;
}

.content-box-grid {
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto;
  row-gap: 2rem;
  grid-template-areas: 'header' 'visual' 'content';

  &.visual-first {
    grid-template-areas: 'visual' 'header' 'content';

    &.no-body {
      grid-template-rows: auto auto;
      grid-template-areas: 'visual' 'header';
    }
  }

  &.single-column {
    grid-template-rows: auto auto;
    grid-template-areas: 'header' 'content';

    &.no-body {
      grid-template-rows: auto;
      grid-template-areas: 'header';
    }
  }

  &.no-body {
    grid-template-rows: auto auto;
    grid-template-areas: 'header' 'visual';
  }
}

.full-image {
  padding-top: 100%;

  @supports (aspect-ratio: 1 / 1) {
    aspect-ratio: 1 / 1;
    padding-top: 0;
  }
}

@screen md {
  .full-image {
    aspect-ratio: auto;
    padding-top: 0;
  }

  .content-box-grid {
    grid-template-areas: 'visual' 'header' 'content';

    &.single-column {
      grid-template-rows: auto auto;
      grid-template-areas: 'header' 'content';

      &.no-body {
        grid-template-rows: auto;
        grid-template-areas: 'header';
      }
    }

    &.no-body {
      grid-template-rows: auto auto;
      grid-template-areas: 'header' 'visual';
    }
  }
}

@screen lg {
  .content-box-grid {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    grid-template-areas: 'visual header' 'visual content';

    &.visual-right {
      grid-template-areas: 'header visual' 'content visual';

      &.no-body {
        grid-template-areas: 'header visual' 'header visual';
      }
    }

    &.single-column {
      @apply w-6/12 mx-auto;
      grid-template-columns: 1fr;
      grid-template-areas: 'header' 'content';

      &.no-body {
        grid-template-rows: auto;
        grid-template-areas: 'header';
      }
    }

    &.no-body {
      grid-template-rows: auto;
      grid-template-areas: 'visual header';

      .content-box-header {
        margin-top: auto !important;
        margin-bottom: auto;
      }
    }
  }
}

ol,
ul {
  counter-reset: content-box-list-counter;

  li {
    counter-increment: content-box-list-counter;

    &::before {
      @apply w-em mr-0 absolute left-0;
    }
  }
}

ol li::before {
  content: counter(content-box-list-counter) '.';
  @apply font-druk;
}

ul li::before {
  content: '\e00c';
  font-family: 'majority-dsm';
  margin-top: 2px;
  @apply text-tiny;
}
</style>
